import { useMutation } from "@tanstack/react-query";

export function TextExportActions({
  text,
  filename,
  disabled = false,
}: {
  text: string;
  filename: string;
  disabled?: boolean;
}) {
  const copyMutation = useMutation({
    mutationFn: async (value: string) => {
      await navigator.clipboard.writeText(value);
    },
  });
  const downloadMutation = useMutation({
    mutationFn: async (value: string) => {
      const url = URL.createObjectURL(
        new Blob([value], { type: "text/plain;charset=utf-8" }),
      );
      const revokeObjectURL = URL.revokeObjectURL.bind(URL);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.append(link);
      try {
        link.click();
      } finally {
        link.remove();
        window.setTimeout(() => revokeObjectURL(url), 1_000);
      }
    },
  });
  const copyFailed = copyMutation.isError && copyMutation.variables === text;
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          disabled={disabled || !text || copyMutation.isPending}
          onClick={() => copyMutation.mutate(text)}
          className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
        >
          Copier en texte
        </button>
        <button
          type="button"
          disabled={disabled || !text || downloadMutation.isPending}
          onClick={() => downloadMutation.mutate(text)}
          className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
        >
          Télécharger en .txt
        </button>
      </div>
      <div role="status" className="text-sm">
        {copyMutation.isSuccess && copyMutation.variables === text ? (
          <p className="text-emerald-700">
            Texte copié dans le presse-papiers.
          </p>
        ) : null}
        {copyFailed ? (
          <p className="text-red-800">
            Impossible de copier le texte automatiquement. Vous pouvez le
            sélectionner ci-dessous ou télécharger le fichier.
          </p>
        ) : null}
        {downloadMutation.isError && downloadMutation.variables === text ? (
          <p className="text-red-800">
            Impossible de télécharger le fichier. Vous pouvez copier le texte.
          </p>
        ) : null}
      </div>
      {copyFailed ? (
        <textarea
          aria-label="Texte de la commande à copier"
          readOnly
          value={text}
          onFocus={(event) => event.currentTarget.select()}
          rows={8}
          className="w-full rounded-lg border border-slate-300 p-3 text-sm"
        />
      ) : null}
    </div>
  );
}
